import * as cookieManager from 'globals/cookieManager';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { getSubdomainFromHost } from 'router/navigation';
import { getRootHost } from 'globals/helpers';
import * as Session from 'state/session/helpers';
import * as Storage from 'globals/storage';
import { initHermes, renderLoadingSpinner } from './run';
import { initialiseDataDog } from './services/datadog/datadog-helpers';
import {
  performTenancyLookup,
  getRegionalApiEndpoints,
  lookupKeys,
} from 'globals/regional-api';
import { isUnifiedLoginEnabled } from 'globals/testable';
import { SIGNUP_CODES } from './pages/signup/constants';
import {
  errorCodes,
  parseErrorCode,
} from 'wa-storybook/global/constants/error-messages';
import waFetch from 'globals/wa-fetch';
import {
  getStoredSelectedEnvironment,
  setAPIEndpoints,
} from 'react-components/env-controller/env-controller-context';
import {
  AppLogger,
  LogTypes,
  AuthClient,
  Analytics,
} from '@workivate/tho-web-shared';
import { registerServiceWorker } from 'register-sw';
import { runHermesWithUSL } from './run-with-usl';
import { getUrlParams } from './run-with-usl.utils';
import { getEnabledLogFeatures } from 'react-components/debug-overlay/debug-overlay';
import {
  getIsUnifiedLoginEnabled,
  setUnifiedLoginEnabled,
} from 'globals/unified-login.helpers';
import hermesVersion from 'version';
import './app.scss';

const urlParams = queryString.parse(location.search, {
  parseBooleans: true,
});

AppLogger.configure({ isProduction: IS_PRODUCTION });

/**
 * Handles the setup of debugging options from URL parameters or localStorage.
 * IE: dataDogEnabled, debugLogsOn and specific logging features
 */
export const storeDebugParamsFromUrl = () => {
  if (IS_PRODUCTION) return;
  const enabledLogFeaturesParam = urlParams['enabledLogFeatures'];
  const dataDogEnabledParam = urlParams['dataDogEnabled'];

  // Allow url params to override localStorage values
  if (dataDogEnabledParam !== undefined) {
    localStorage.setItem(
      Storage.storageKeys.dataDogEnabled,
      dataDogEnabledParam,
    );
  }

  let effectiveEnabledLogFeatures;

  if (enabledLogFeaturesParam) {
    effectiveEnabledLogFeatures = JSON.parse(
      enabledLogFeaturesParam as string,
    ) as Record<string, boolean>;
  } else {
    effectiveEnabledLogFeatures = getEnabledLogFeatures();
  }

  // If oauth2 is enabled set the debugLogsOn to true as well
  if (effectiveEnabledLogFeatures?.oauth2) {
    localStorage.setItem(Storage.storageKeys.debugLogsOn, 'true');

    AppLogger.setEnabledFeatures({ oauth2: true });
  } else {
    AppLogger.setEnabledFeatures({ oauth2: false });
  }
  localStorage.setItem(
    Storage.storageKeys.enabledLogFeatures,
    JSON.stringify(effectiveEnabledLogFeatures),
  );
};

storeDebugParamsFromUrl();

const currentDomain = '.' + getRootHost();
const hasUrlParams = !isEmpty(urlParams);
const msTeamsUrl = urlParams['msTeamsReturnUrl'];
const isFromTeams = urlParams['isFromTeams'];

const auth = JSON.parse(cookieManager.getCookie('WAM_AUTH') as string);

const userToken =
  (hasUrlParams && urlParams['userToken']) || (!isEmpty(auth) && auth.wamToken);

registerServiceWorker();

/*
 * We need to logout the email-less SSO user before using a new token
 * LT-6894
 */
const token = hasUrlParams && urlParams['token'];
if (token) {
  Session.clearLogoutStorage();
}

if (userToken || token) {
  // delete Oauth2 login related data to continue with ghost login
  AuthClient.deleteAuthSession();
}

/* We need to set back the token and the timestamp after we clear the previous
 * ones, because we need to read them deeper in the app, and we want to avoid
 * having user tokens as url parameters.
 */
// Only set SSO token if token exists and USL is NOT enabled
// When USL is enabled, SSO authentication is handled by runHermesWithUSL
if (token && !isUnifiedLoginEnabled()) {
  Session.setSSOToken(token as string); // TODO remove this as part of removing the pre-USL login system
}
Session.setMsTeamsUrl(msTeamsUrl as string);
Session.setIsFromTeams(!!isFromTeams);

/**
 * Default debugLogsOn to true in non-production environments and if it's not set
 */
if (
  !IS_PRODUCTION &&
  localStorage.getItem(Storage.storageKeys.debugLogsOn) === null
) {
  localStorage.setItem(Storage.storageKeys.debugLogsOn, 'true');
}

/*
Initialise Datadog - should be enabled by default for test and prod,
for development and integration we respect the localStorage value.
*/
const dataDogEnabled =
  localStorage.getItem(Storage.storageKeys.dataDogEnabled) === 'true';

const shouldEnableByDefault = IS_PRODUCTION || IS_TEST;
const shouldRespectURLPreference = IS_DEVELOPMENT || IS_INTEGRATION;
const userWantsDataDog = dataDogEnabled && shouldRespectURLPreference;

if (shouldEnableByDefault || userWantsDataDog) {
  initialiseDataDog();
  AppLogger.logWithOptions({
    message: 'Datadog initialised',
    level: LogTypes.Info,
  });
}

// Cache URL params (including auth tokens) before removing them from the URL.
// This allows run-with-usl.tsx to still access tokens via the cache after cleanup.
getUrlParams();

/*
 * We need to remove url parameters early to avoid leaking tokens to analytics.
 * Must be called before injectGoogleTagManager, which skips injection if forbidden params are present.
 */
Analytics.removeForbiddenParameters({});

window[`ga-disable-${window.WAM.ENV.analyticsMeasurementId}`] = true;

/*
 * Only inject GA after we remove the forbidden url params to avoid sending them.
 */
Analytics.injectGoogleTagManager({
  measurementId: window.WAM.ENV.analyticsMeasurementId,
});

// We do a Call to the API directly because if there's a token, it comes from the API Auth system
export const loginFromToken = (wamToken, domain = '.' + getRootHost()) => {
  const onAuthResponse = ({ body: authMobileResponse, error }) => {
    if (
      error &&
      error.code === parseErrorCode(errorCodes['session_timeout'][0])
    ) {
      return Promise.reject(error);
    }

    const {
      token,
      user,
      company, // TODO: Company data from auth/mobile endpoint is incomplete so it is not used anymore, a new fetch it is in place
      mfa,
      signup_state: signupState,
    } = authMobileResponse || {};

    if (signupState === SIGNUP_CODES.ADMIN_CONFIRMATION_REQUIRED) {
      initHermes();

      return;
    }

    Session.setToken(token);

    Storage.set(Storage.getKeys().tokenExpiryTime, user['token_expiry_time']);

    // TODO: This is the only place where company data from auth is used, we can get rid of it
    // as we already make a separate get for company data, solution: remove company data from endpoint and keep just 'wa_subdomain'
    // when starting MFA setup flow company is not returned
    const companySubdomain = !mfa && company['wa_subdomain'];

    // If not correct subdomain and not starting MFA setup flow - redirect
    if (!mfa && (!companySubdomain || subdomain !== companySubdomain)) {
      Session.handleLoginRedirect(companySubdomain);
    } else {
      cookieManager.deleteCookie('WAM_AUTH', domain);

      initHermes({ user, mfa });
      return;
    }
  };

  // Used when:
  // - natives open the app with a token (wamToken),
  // - during login when redirected from different subdomain's login page (wamToken),
  // - hwr user logs in (wamToken),
  // - admin clicks on 'visit network' link (ott),

  const {
    subdomain: queryStringSubdomain,
    setEnvironment: setEnvFromQueryString,
  } = queryString.parse(location.search);

  const setEnvironment =
    setEnvFromQueryString ?? getStoredSelectedEnvironment() ?? null;
  const subdomain = getSubdomainFromHost();

  if (setEnvironment && (!IS_PRODUCTION || !IS_TEST)) {
    setAPIEndpoints(setEnvironment);
  }

  return performTenancyLookup(
    lookupKeys.domain,
    (queryStringSubdomain as string) || subdomain,
  )
    .then(() => {
      const headers = {
        Accept: 'application/vnd.wam-api-v1.3+json',
        'Content-Type': 'application/json',
      };

      if (urlParams['ott']) {
        headers['One-Time-Token'] = wamToken;
      } else if (wamToken) {
        headers['Wam-Token'] = wamToken;
      }

      return waFetch(
        `${getRegionalApiEndpoints().waapi_root}/auth/mobile`,
        {
          method: 'post',
          headers,
        },
        true,
        false,
      );
    })
    .then(onAuthResponse)
    .catch(res => {
      if (res.body?.mfa) {
        onAuthResponse(res);
      }
    });
};

const initialiseApp = async () => {
  renderLoadingSpinner();

  try {
    // Performs a series of checks to determine if Unified Login is enabled.
    const uslEnabled = getIsUnifiedLoginEnabled();
    setUnifiedLoginEnabled(uslEnabled);

    // Determine login persistence (whether to store auth credentials in localStorage or sessionStorage)
    let effectivePersistence: boolean; // App decides persistence based on URL param or stored preference
    // SSO, SSOX, userToken (deeplink) all use the default
    // Only explicit username / password login currently sets persistence to false

    const persistenceParam = urlParams?.['persistence'];

    if (persistenceParam !== undefined) {
      // URL parameter takes precedence; can be string or boolean
      effectivePersistence =
        persistenceParam === true || persistenceParam === 'true';
    } else {
      // Read stored preference from localStorage (on refresh page or new tab when already logged in, check previous choice)
      // Default to true if no stored preference exists (first-time login)
      const storedPersistence = Storage.getPersistence();
      effectivePersistence = storedPersistence ?? true;
    }

    // Ensure Hermes looks for auth credentials in the correct storage based on persistence choice
    Storage.setPersistence(effectivePersistence);

    if (isUnifiedLoginEnabled()) {
      AuthClient.configure({
        authEndpoint: `https://${window.WAM.ENV.awsCognitoSSODomain}`,
        redirectUri: Session.getOauth2RedirectUri(),
        environment: Session.getEnvironmentForHeader(),
        appName: 'web',
        appVersion: hermesVersion.version,
        persistence: effectivePersistence, // whether login session will persist across browser sessions
      });

      runHermesWithUSL();
    } else if (userToken) {
      loginFromToken(userToken, currentDomain);
    } else {
      initHermes();
    }
  } catch (error) {
    console.error('Error during app initialisation:', error);
    // Fallback initialisation
    initHermes();
  }
};

initialiseApp();
